import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import clouds from 'assets/images/import-clouds.svg'
import { OpenPositionFragment } from 'generated/resources/graphql'
import { isEmpty, omitBy } from 'lodash'

import { Authorized } from 'modules/core/components/Authorized'
import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { useForm } from 'modules/core/hooks/useForm'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'

import { useCreateInternalApplication } from './useCreateInternalApplication'

interface NewCandidateFormValues {
  atsJobPostingId: number
  firstName: string
  lastName: string
  email: string
  phone?: string
  cv: File | undefined
}

interface Props {
  openPosition: OpenPositionFragment
  onClose: () => void
}

export const InternalApplyModal = ({ openPosition, onClose }: Props) => {
  const { create } = useCreateInternalApplication()
  const {
    access,
    email,
    company: { subscription },
  } = useCurrentSession()

  const { values, setValues, handleSubmit, isSubmitting, isValid, errors } =
    useForm<NewCandidateFormValues>({
      defaultValues: {
        atsJobPostingId: openPosition.id,
        email: email ?? '',
        firstName: access.firstName,
        lastName: access.lastName,
        phone: '',
        cv: undefined,
      },
      validate: (data) => {
        const formErrors: FormErrors<NewCandidateFormValues> = {}

        if (!data.firstName) {
          errors.firstName = i18n.t('common.validations.empty')
        }

        if (!data.lastName) {
          errors.lastName = i18n.t('common.validations.empty')
        }

        if (!data.cv) {
          formErrors.cv = i18n.t('common.validations.empty')
        }

        return formErrors
      },
      onSubmit: async (data, { setErrors }) => {
        const result = await create({
          ...omitBy(data, isEmpty),
          atsJobPostingId: data.atsJobPostingId,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          cv: data.cv,
        })

        const handled = handleResult(result, (responseErrors) => {
          setErrors(responseErrors)
        })

        if (handled.success && handled.data.application) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
          onClose()
        }

        return handled
      },
    })

  const onUploadedFile = (files: File[]) => {
    setValues({
      ...values,
      cv: files[0],
    })
  }

  const onRemoveFile = () => {
    setValues({
      ...values,
      cv: undefined,
    })
  }

  return (
    <Modal
      illustration="id"
      title={i18n.t('ats.open_positions.internal_apply_modal.title', {
        jobTitle: openPosition.title,
      })}
      description={i18n.t('ats.open_positions.internal_apply_modal.description')}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={isSubmitting || !isValid} submit>
            {i18n.t('common.submit')}
          </Button>
        </>
      }
      onClose={onClose}
      onSubmit={() => handleSubmit()}
    >
      <Box gap="s16">
        <TextField label={i18n.t('ats.jobs_form.first_name')} value={values.firstName} readonly />
        <TextField label={i18n.t('ats.jobs_form.last_name')} value={values.lastName} readonly />
        <TextField
          label={i18n.t('ats.jobs_form.email')}
          value={values.email}
          error={errors.email}
          readonly
        />
        <TextField
          label={i18n.t('ats.jobs_form.phone')}
          value={values.phone}
          onChange={(value) =>
            setValues({
              ...values,
              phone: value,
            })
          }
        />
        <Box gap="s4">
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('ats.jobs_form.cv')}
            {errors.cv && (
              <Box>
                <Text.Normal size="200" color="radicalRed1000">
                  {errors.cv}
                </Text.Normal>
              </Box>
            )}
          </Text.Normal>
          {values.cv ? (
            <Box
              border={{
                all: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              }}
              borderRadius={{ all: 'abs008' }}
              paddingX="s24"
              paddingY="s16"
            >
              <Box
                alignItems="center"
                justifyContent="spaceBetween"
                flexGrow
                flexDirection="row"
                gap="s16"
              >
                <Box flexDirection="row" alignItems="center" gap="s16">
                  <DocumentType mimetype={values.cv.type} />
                  <Box overflowX="hidden" maxWidth="s240">
                    <Text.Normal weight="medium" ellipsis overflowWrap>
                      {values.cv.name}
                    </Text.Normal>
                  </Box>
                </Box>
                <Button type="secondary" onClick={() => onRemoveFile()} iconName="delete" />
              </Box>
            </Box>
          ) : (
            <Authorized subscription={subscription}>
              <DropzoneBox
                onDrop={onUploadedFile}
                icon={clouds}
                multiple={false}
                message={i18n.t('documents.drop_here')}
                accept={acceptedContentTypes()}
              />
            </Authorized>
          )}
        </Box>
      </Box>
    </Modal>
  )
}
